// Ported from relay-protocol packages/relay-widget (v0.1.0).
// Headless selection state for the user-selectable CROSS delivery token
// (Task 9). Consumes Task 8's `Catalog.targets` -- this hook does NOT fetch
// anything itself, it purely derives `options` (the catalog intersected with
// an optional host-supplied `filter`) and manages which one is `selected`,
// so <RelayDeposit> can pass `selected.symbol` into useDepositAddress ->
// client.getDepositAddress's `target` param.
import { useMemo, useState } from 'react'
import type { Target } from '../lib/types'

export interface UseTargetTokensOptions {
  /** The full set of delivery targets from `Catalog.targets` (Task 8). */
  catalogTargets: Target[]
  /**
   * Restrict `options` to these symbols (a host-supplied allowlist, e.g. the
   * RelayDeposit `targetTokens` prop). Absent or empty -- ALL catalog targets
   * are offered, preserving catalog order. Any filter symbol absent from the
   * catalog is silently ignored (never throws, never adds a phantom option).
   */
  filter?: string[]
  /**
   * Symbol to select initially, e.g. the RelayDeposit `defaultTarget` prop.
   * Falls back gracefully (never throws) when the symbol isn't present in
   * `options`: the catalog's own default (`isDefault: true`) wins next, then
   * the first option, then `undefined` when `options` is empty.
   */
  defaultTarget?: string
}

export interface UseTargetTokensResult {
  /** `catalogTargets` intersected with `filter`, in catalog order. */
  options: Target[]
  selected: Target | undefined
  /** Updates the selection. A symbol not present in `options` is ignored. */
  setSelected: (symbol: string) => void
}

function pickInitial(options: Target[], defaultTarget?: string): Target | undefined {
  if (defaultTarget) {
    const match = options.find((t) => t.symbol === defaultTarget)
    if (match) return match
  }
  return options.find((t) => t.isDefault) ?? options[0]
}

export function useTargetTokens({
  catalogTargets,
  filter,
  defaultTarget,
}: UseTargetTokensOptions): UseTargetTokensResult {
  const options = useMemo(() => {
    if (!filter || filter.length === 0) return catalogTargets
    const allow = new Set(filter)
    return catalogTargets.filter((t) => allow.has(t.symbol))
    // `catalogTargets` is expected to be a stable reference per catalog load
    // (see Catalog.targets / getConfig) -- re-deriving on every render of an
    // inline array literal is intentionally avoided by callers, not guarded
    // against here.
  }, [catalogTargets, filter])

  const [selectedSymbol, setSelectedSymbol] = useState<string | undefined>(
    () => pickInitial(options, defaultTarget)?.symbol,
  )

  // Resolve the current selection against `options` every render (rather than
  // storing the Target object in state) so a later catalog/filter change that
  // drops the previously-selected symbol falls back gracefully instead of
  // holding a stale Target no longer in `options`.
  const selected =
    (selectedSymbol !== undefined ? options.find((t) => t.symbol === selectedSymbol) : undefined) ??
    pickInitial(options, defaultTarget)

  function setSelected(symbol: string) {
    if (!options.some((t) => t.symbol === symbol)) return
    setSelectedSymbol(symbol)
  }

  return { options, selected, setSelected }
}
